import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { EndClientReportService } from './end-client-report.service';
import type { DeckLoad } from './end-client-report.service';
import { blockVariableKeys, parseDatamap, unresolvedVariables } from './report-aggregation';
import type { DecipherDatamap, RespondentRecord, SurveyLayout } from './report-aggregation';
import { unpackBundle } from './report-bundle';
import type { ReportBundle } from './report-bundle';
import type { EndClientReport, ReportDesign } from '../../models/end-client-reporting-package';
import { environment } from '../../environments/environment';

/** Why a share link didn't open. Each says something different to whoever followed it. */
export type ShareLinkFailure = 'notFound' | 'expired' | 'revoked' | 'wrongDomain' | 'notReady' | 'unknown';

/**
 * The deck's data, read from a published bundle instead of the API (NK-399).
 *
 * **It stands in for `EndClientReportService`**, provided in its place on the viewer route, so the
 * deck component loads exactly as it always does and knows nothing about share links. That is the
 * same trick as the unpacking itself: keep one implementation of the thing that matters — here the
 * deck, there the counting — and vary what feeds it.
 *
 * Only the four reads the deck performs on load are answered. Everything else it inherits would need
 * an authenticated session, and nothing in a client view calls them: the controls are gone, and
 * `REPORT_VIEW_MODE` keeps them gone.
 */
@Injectable()
export class ShareLinkReportService extends EndClientReportService {
  private shareUrl = environment.apiEndpoint + '/ecreport/';

  private report: EndClientReport | null = null;
  private design: ReportDesign | null = null;
  private records: RespondentRecord[] = [];
  private layout: SurveyLayout = {};
  /** The link being viewed, kept for the images that are fetched after the bundle. */
  private deploymentId = '';
  private accessKey = '';

  constructor(private httpClient: HttpClient) {
    super(httpClient);
  }

  // Not through the browser cache: this isn't the logged-in user's report data.
  override loadDeck(reportId: string): Observable<DeckLoad> {
    return this.loadDeckUncached(reportId);
  }

  /**
   * Fetches the bundle and unpacks it. Everything the deck later asks for is already in memory by
   * the time it asks, which is why the viewer renders the deck only once this has completed.
   */
  loadBundle(deploymentId: string, accessKey: string): Observable<void> {
    this.deploymentId = deploymentId;
    this.accessKey = accessKey;
    return this.httpClient
      .get<ReportBundle>(`${this.shareUrl}${deploymentId}/bundle`, { params: { k: accessKey } })
      .pipe(
        tap(bundle => this.accept(bundle)),
        map(() => undefined),
        catchError((error: HttpErrorResponse) => throwError(() => this.failureOf(error))),
      );
  }

  override getReport(): Observable<EndClientReport> {
    return of(this.report as EndClientReport);
  }

  override getReportDesign(): Observable<ReportDesign> {
    return of(this.design as ReportDesign);
  }

  override getRecords(): Observable<RespondentRecord[]> {
    return of(this.records);
  }

  override getSurveyLayout(): Observable<SurveyLayout> {
    return of(this.layout);
  }

  // The images frozen beside the bundle at deploy, behind the same id and key. Not the base
  // service's endpoints: those need a signed-in user, and serve the live report rather than the
  // one that was published.
  override getQuestionImage(_reportId: string, questionLabel: string): Observable<Blob | null> {
    return this.sharedImage(`question/${encodeURIComponent(questionLabel)}`);
  }

  override getUploadedImage(_reportId: string, imageId: string): Observable<Blob | null> {
    return this.sharedImage(encodeURIComponent(imageId));
  }

  // The deck's logo, which the deploy froze among the uploads under its own id.
  override getLogo(imageId: string): Observable<Blob | null> {
    return this.sharedImage(encodeURIComponent(imageId));
  }

  private sharedImage(path: string): Observable<Blob | null> {
    if (!this.deploymentId) return of(null);
    return this.httpClient
      .get(`${this.shareUrl}${this.deploymentId}/heatmap-image/${path}`, {
        params: { k: this.accessKey },
        responseType: 'blob',
      })
      .pipe(catchError(() => of(null)));
  }

  private accept(bundle: ReportBundle): void {
    const unpacked = unpackBundle(bundle);

    this.records = unpacked.records;
    this.layout = parseDatamap(unpacked.datamap as DecipherDatamap);
    this.design = withoutUnresolvedBlocks(unpacked.design, this.layout);

    // The deck reads a report row for its name, description and version. Only the first two are
    // rendered in a client view; the version belongs to saving, which cannot happen here.
    this.report = {
      id: '',
      projectId: '',
      name: unpacked.report?.name ?? '',
      description: unpacked.report?.description ?? '',
      reportDesignVersion: 0,
    } as EndClientReport;
  }

  private failureOf(error: HttpErrorResponse): ShareLinkFailure {
    const reason = (error?.error as { reason?: string } | null)?.reason;
    switch (reason) {
      case 'expired': return 'expired';
      case 'revoked': return 'revoked';
      case 'wrongDomain': return 'wrongDomain';
      case 'notReady': return 'notReady';
      case 'notFound': return 'notFound';
      default: return error?.status === 404 ? 'notFound' : 'unknown';
    }
  }
}

/**
 * Drops the blocks whose questions this data doesn't have.
 *
 * A client gets a shorter deck rather than a deck of "no data for …" panels: the gap is Navigator's
 * problem, and the person who can do something about it is told in the share dialog instead. The
 * design is only trimmed for rendering — the bundle still records what was left out.
 */
export function withoutUnresolvedBlocks(design: ReportDesign, layout: SurveyLayout): ReportDesign {
  const missing = new Set(unresolvedVariables(design, layout));
  if (!missing.size) return design;

  // A block goes when any of its questions is missing: a chart with half its series gone states
  // something the report never meant to say. A subsection emptied by that goes too, and so does a section
  // left with no subsections — a nav entry leading to nothing is worse than a shorter deck.
  const sections: ReportDesign['sections'] = {};
  const sectionOrder: string[] = [];

  for (const sectionId of design.sectionOrder ?? []) {
    const section = design.sections?.[sectionId];
    if (!section) continue;

    const pages: typeof section.pages = {};
    const pageOrder: string[] = [];

    for (const subSectionId of section.pageOrder ?? []) {
      const page = section.pages?.[subSectionId];
      if (!page) continue;

      const kept = (page.pageSectionOrder ?? [])
        .filter(blockId => {
          const block = page.pageSections?.[blockId];
          return !!block && !blockVariableKeys(block).some(key => missing.has(key));
        });
      if (!kept.length) continue;

      pages[subSectionId] = {
        ...page,
        pageSections: Object.fromEntries(kept.map(blockId => [blockId, page.pageSections[blockId]])),
        pageSectionOrder: kept,
      };
      pageOrder.push(subSectionId);
    }

    if (!pageOrder.length) continue;

    sections[sectionId] = { ...section, pages, pageOrder };
    sectionOrder.push(sectionId);
  }

  return { ...design, sections, sectionOrder };
}
